<template>
  <section class="mt-12 pb-6">
    <h3 class="mb-2 text-[1.3rem] text-neutral-650">{{ wt('projectSubmission', 'planningEnd', 'title') }}</h3>
    <p class="mb-6 text-sm text-neutral-450">{{ wt('projectSubmission', 'planningEnd', 'description') }}</p>

    <div class="grid grid-cols-1 gap-x-10 md:grid-cols-2">
      <!-- Year -->
      <div class="flex flex-col">
        <label class="core-form-label" for="pe-year">
          {{ wt('projectSubmission', 'planningEnd', 'year') }}
        </label>
        <NumberInput
          id="pe-year"
          v-model="form.year"
          :maxLength="4"
          :placeholder="wt('projectSubmission', 'planningEnd', 'yearPlaceholder')"
          class="core-form-input core-form-input--full"
          :class="{ 'core-form-error': errors['planningEnd.year'] }" />
        <p v-if="errors['planningEnd.year']" class="text-sm text-red-400 mb-4">
          {{ errors['planningEnd.year'] }}
        </p>
      </div>

      <!-- Construction Costs -->
      <div class="flex flex-col">
        <label class="core-form-label" for="pe-construction-costs">
          {{ wt('projectSubmission', 'planningEnd', 'constructionCosts') }}
        </label>
        <div class="flex items-center">
          <NumberInput
            id="pe-construction-costs"
            v-model="form.constructionCosts"
            :formatBigNumber="true"
            :placeholder="wt('projectSubmission', 'planningEnd', 'constructionCostsPlaceholder')"
            class="core-form-input core-form-input--full text-right" />
          <span class="mb-7 ml-2 shrink-0 text-lg text-neutral-550">{{ currency }}</span>
        </div>
      </div>
    </div>

    <!-- Per-role fields -->
    <div v-for="role in validRoles" :key="role" class="mt-6">
      <h4 class="core-form-role-title text-[1.2rem]">{{ wtGeneral('roleParts', role) }}</h4>

      <div class="grid grid-cols-1 gap-x-10 md:grid-cols-2">
        <!-- Effort-determining costs -->
        <div class="flex flex-col">
          <label class="core-form-label">
            {{ wt('projectSubmission', 'planningEnd', 'effortDeterminingCosts') }}
          </label>
          <div class="flex items-center">
            <NumberInput
              v-model="form.roleData[role].effortDeterminingCosts"
              :formatBigNumber="true"
              :placeholder="wt('projectSubmission', 'planningEnd', 'effortDeterminingCostsPlaceholder')"
              class="core-form-input core-form-input--full text-right" />
            <span class="mb-7 ml-2 shrink-0 text-lg text-neutral-550">{{ currency }}</span>
          </div>
        </div>

        <!-- Service share -->
        <div class="flex flex-col">
          <label class="core-form-label">
            {{ wt('projectSubmission', 'planningEnd', 'serviceShare') }}
          </label>
          <div class="flex items-center">
            <NumberInput
              v-model="form.roleData[role].serviceShare"
              :placeholder="wt('projectSubmission', 'planningEnd', 'serviceSharePlaceholder')"
              class="core-form-input core-form-input--full text-right"
              :class="{ 'core-form-error': errors[`planningEnd.serviceShare.${role}`] }" />
            <span class="mb-7 ml-2 shrink-0 text-lg text-neutral-550">%</span>
          </div>
          <p v-if="errors[`planningEnd.serviceShare.${role}`]" class="text-sm text-red-400 mb-4">
            {{ errors[`planningEnd.serviceShare.${role}`] }}
          </p>
        </div>

        <!-- Invoiced hours -->
        <div class="flex flex-col">
          <label class="core-form-label">
            {{ wt('projectSubmission', 'planningEnd', 'invoicedHours') }}
          </label>
          <div class="flex items-center">
            <NumberInput
              v-model="form.roleData[role].invoicedHours"
              :formatBigNumber="true"
              :placeholder="wt('projectSubmission', 'planningEnd', 'invoicedHoursPlaceholder')"
              class="core-form-input core-form-input--full text-right" />
            <span class="mb-7 ml-2 shrink-0 text-lg text-neutral-550">h</span>
          </div>
        </div>

        <!-- Effective hours -->
        <div class="flex flex-col">
          <label class="core-form-label">
            {{ wt('projectSubmission', 'planningEnd', 'effectiveHours') }}
          </label>
          <div class="flex items-center">
            <NumberInput
              v-model="form.roleData[role].effectiveHours"
              :formatBigNumber="true"
              :placeholder="wt('projectSubmission', 'planningEnd', 'effectiveHoursPlaceholder')"
              class="core-form-input core-form-input--full text-right" />
            <span class="mb-7 ml-2 shrink-0 text-lg text-neutral-550">h</span>
          </div>
        </div>
      </div>

      <div class="mt-4 flex flex-col">
        <label class="core-form-label">
          {{ getDifficultyAdjustedQuestionLabel(role) }}
        </label>
        <section class="mb-4 mt-2 boxed-button-grid">
          <label class="base-button" :class="{ active: !hasDifficultyAdjustment(role) }">
            {{ wt('projectSubmission', 'planningEnd', 'difficultyAdjustedNo') || 'Nein' }}
            <input
              type="radio"
              :name="`planning-end-difficulty-adjusted-${role}`"
              :checked="!hasDifficultyAdjustment(role)"
              @change="setDifficultyAdjusted(role, false)" />
          </label>
          <label class="base-button" :class="{ active: hasDifficultyAdjustment(role) }">
            {{ wt('projectSubmission', 'planningEnd', 'difficultyAdjustedYes') || 'Ja' }}
            <input
              type="radio"
              :name="`planning-end-difficulty-adjusted-${role}`"
              :checked="hasDifficultyAdjustment(role)"
              @change="setDifficultyAdjusted(role, true)" />
          </label>
        </section>

        <label class="core-form-label" :class="{ 'opacity-60': !hasDifficultyAdjustment(role) }">
          {{ getDifficultyLabel(role) }}
        </label>
        <div :class="{ 'opacity-75': !hasDifficultyAdjustment(role) }">
          <RangeInput
            :modelValue="getDifficultyValue(role)"
            :min="getDifficultyOptions(role)[0]"
            :max="getDifficultyOptions(role)[getDifficultyOptions(role).length - 1]"
            :step="0.1"
            :colorVariant="hasDifficultyAdjustment(role) ? 'primary' : 'neutral'"
            :disabled="!hasDifficultyAdjustment(role)"
            :thumbIcon="hasDifficultyAdjustment(role) ? undefined : 'lock'"
            @update:modelValue="form.roleData[role].difficultyFactor = $event" />
          <div class="mb-4 -mt-2 flex justify-between text-sm text-neutral-450 lg:-mt-6">
            <span v-for="opt in getDifficultyOptions(role)" :key="opt">{{ opt }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'
import NumberInput from '@/components/partials/inputs/NumberInput.vue'
import RangeInput from '@/components/partials/inputs/RangeInput.vue'
import { useWidgetText } from '@/helpers/useWidgetText'
import { getCurrencyForCountry } from '@/helpers/format'
import {
  getSubmissionDifficultyAdjustedQuestion,
  getSubmissionDifficultyLabel,
  getSubmissionDifficultyOptions,
} from '@/helpers/submission-difficulty'

const { wt } = useWidgetText('widget-texts-project-summary')
const { wt: wtGeneral } = useWidgetText('widget-texts-general')

interface PlanningEndRoleData {
  difficultyFactor: number | null
  effortDeterminingCosts: number | null
  serviceShare: number | null
  invoicedHours: number | null
  effectiveHours: number | null
}

interface PlanningEndForm {
  year: number | null
  constructionCosts: number | null
  roleData: Record<string, PlanningEndRoleData>
}

const form = defineModel('form', { type: Object as PropType<PlanningEndForm>, required: true })

const props = defineProps<{
  validRoles: string[]
  country: string
  errors: Record<string, string>
  planningStartRoleData: Record<string, { difficultyFactor: number | null }>
}>()

const currency = computed(() => getCurrencyForCountry(props.country))

function hasDifficultyAdjustment(role: string): boolean {
  return form.value.roleData[role].difficultyFactor != null
}

function getPlanningStartDifficulty(role: string): number {
  return props.planningStartRoleData[role]?.difficultyFactor ?? 1
}

function getDifficultyValue(role: string): number {
  return form.value.roleData[role].difficultyFactor ?? getPlanningStartDifficulty(role)
}

function setDifficultyAdjusted(role: string, adjusted: boolean): void {
  form.value.roleData[role].difficultyFactor = adjusted ? getPlanningStartDifficulty(role) : null
}

function getDifficultyOptions(role: string): number[] {
  return getSubmissionDifficultyOptions(role)
}

function getDifficultyLabel(role: string): string {
  return getSubmissionDifficultyLabel(role, wt, 'planningEnd')
}

function getDifficultyAdjustedQuestionLabel(role: string): string {
  return getSubmissionDifficultyAdjustedQuestion(role, wt)
}
</script>
